"use client";

import { useEffect, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import styles from "./arena.module.css";
import { prefersReducedMotion } from "./Reveal";
import {
  CONSOLE_HIDDEN,
  CONSOLE_SAMPLES,
  CONSOLE_SOURCES,
  CONSOLE_TESTS,
  CONSOLE_TIMELINE,
  consoleLines,
  consoleState,
  type ConsoleLanguage,
  type TokenKind,
} from "./judge-console";

const TOKEN_CLASS: Readonly<Record<TokenKind, string | undefined>> = {
  pp: styles.tPp,
  kw: styles.tKw,
  ty: styles.tTy,
  fn: styles.tFn,
  st: styles.tSt,
  nu: styles.tNu,
  op: styles.tOp,
  id: styles.tId,
  ns: styles.tNs,
  ws: undefined,
};

const LANGUAGES: readonly ConsoleLanguage[] = ["cpp", "python"];

/** The first run waits this long after mount, so the page has settled before anything moves. */
const FIRST_RUN_DELAY_MS = 800;
/** Far enough past the end of the timeline to read as "finished" under reduced motion. */
const FINISHED = CONSOLE_TIMELINE.accepted + 1;

/**
 * The hero's judge console (`EX-PUB19`). One run plays on its own when the
 * page opens, under five seconds, then rests on Accepted; after that it moves
 * only when the visitor presses Submit or opens the other file. Under reduced
 * motion every run lands straight on its result.
 *
 * The clock ticks only while a run is in flight, so a finished console costs
 * nothing.
 */
export function JudgeConsole() {
  const t = useTranslations("judge.home");
  const tJudge = useTranslations("judge");
  const tCatalog = useTranslations("judge.catalog");

  const [language, setLanguage] = useState<ConsoleLanguage>("cpp");
  const [runStartedAt, setRunStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const [still, setStill] = useState(false);
  /** Set by the visitor's own Submit: only a run they started is spoken. */
  const [announce, setAnnounce] = useState(false);

  useEffect(() => {
    const reduced = prefersReducedMotion();
    setStill(reduced);
    const started = Date.now() + (reduced ? -FINISHED : FIRST_RUN_DELAY_MS);
    setRunStartedAt(started);
    setNow(Date.now());
  }, []);

  const elapsed = runStartedAt === null ? null : now - runStartedAt;
  const state = consoleState(elapsed);
  const running = runStartedAt !== null && !still && state.phase !== "accepted";

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, [running]);

  const lines = useMemo(() => consoleLines(language), [language]);
  const source = CONSOLE_SOURCES[language];
  const busy = state.phase === "queued" || state.phase === "compiling" || state.phase === "running";

  function submit() {
    const at = Date.now();
    setRunStartedAt(still ? at - FINISHED : at - CONSOLE_TIMELINE.queued);
    setNow(at);
    setAnnounce(true);
  }

  function pick(next: ConsoleLanguage) {
    if (next === language) return;
    setLanguage(next);
    setRunStartedAt(null);
  }

  const samplesDone = Math.min(state.testsDone, CONSOLE_SAMPLES);
  const hiddenDone = Math.max(0, state.testsDone - CONSOLE_SAMPLES);

  /**
   * What a screen reader hears: each phase of a run the visitor started, once.
   * Never the automatic run on page load, and never a test count, which
   * changes five times a second.
   */
  const liveMessage = !announce
    ? ""
    : state.phase === "queued"
      ? tJudge("statusQueued")
      : state.phase === "compiling"
        ? t("consoleCompiling")
        : state.phase === "running"
          ? tJudge("statusRunning")
          : state.phase === "accepted"
            ? `${tJudge("verdict.accepted")}. ${t("consoleResult", { samples: CONSOLE_SAMPLES, hidden: CONSOLE_HIDDEN })}`
            : "";

  return (
    <section
      aria-label={t("consoleLabel")}
      className="relative overflow-hidden rounded-xl border border-white/[0.09] bg-neutral-900 text-neutral-200"
    >
      <div className="flex h-12 items-center justify-between gap-3 border-b border-white/[0.07] bg-white/[0.02] pl-2 pr-3.5">
        <div className="flex min-w-0 items-center gap-1" role="group" aria-label={t("consoleFiles")}>
          {LANGUAGES.map((option) => (
            <Button
              key={option}
              type="button"
              variant="ghostDark"
              onClick={() => pick(option)}
              aria-pressed={language === option}
              className={cn(
                styles.mono,
                "h-8 gap-2 rounded-lg px-3 text-[12.5px] font-normal",
                language === option ? "bg-neutral-800 text-neutral-100" : "text-neutral-400",
              )}
            >
              <span
                className={cn("h-2 w-2 rounded-sm", language === option ? "bg-primary" : "bg-neutral-600")}
                aria-hidden
              />
              {CONSOLE_SOURCES[option].file}
            </Button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={cn(
              styles.mono,
              "hidden h-[26px] items-center rounded-full border border-white/10 px-2.5 text-xs text-neutral-300 sm:inline-flex",
            )}
          >
            {source.toolchain}
          </span>
          <span className="hidden h-[26px] items-center rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 text-xs font-medium text-emerald-300 sm:inline-flex">
            {tCatalog("difficulty_easy")}
          </span>
        </div>
      </div>

      <div className={cn(styles.mono, styles.code, "overflow-hidden py-3.5")} aria-hidden>
        {lines.map((line) => (
          <div key={line.no} className={styles.line}>
            <span className={styles.lineNo}>{line.no}</span>
            <span className={styles.lineCode}>
              {line.tokens.map((token, index) => (
                <span key={index} className={TOKEN_CLASS[token.kind]}>
                  {token.text}
                </span>
              ))}
            </span>
          </div>
        ))}
      </div>

      <div className="border-t border-white/[0.07] bg-black/20 px-4 pb-4 pt-3.5">
        <p className="sr-only" aria-live="polite">
          {liveMessage}
        </p>
        <div className="flex min-h-9 items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2.5 whitespace-nowrap text-[13px] text-neutral-300">
            {state.phase === "ready" ? (
              <>
                <span className="inline-flex h-7 items-center rounded-full border border-white/15 bg-white/[0.08] px-3 text-xs font-semibold text-white/60">
                  {t("consoleReady")}
                </span>
                <span className={cn(styles.mono, "hidden truncate sm:inline")}>{source.build}</span>
              </>
            ) : null}
            {state.phase === "queued" ? (
              <span className="inline-flex h-7 items-center gap-2 rounded-full border border-white/15 bg-white/[0.08] px-3 text-xs font-semibold text-white/60">
                <span className={styles.queueDots} aria-hidden>
                  <span />
                  <span />
                  <span />
                </span>
                {tJudge("statusQueued")}
              </span>
            ) : null}
            {state.phase === "compiling" ? (
              <>
                <span className="inline-flex h-7 items-center gap-2 rounded-full border border-white/15 bg-white/[0.08] px-3 text-xs font-semibold text-white/60">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden />
                  {t("consoleCompiling")}
                </span>
                <span className={cn(styles.mono, "hidden truncate sm:inline")}>{source.build}</span>
              </>
            ) : null}
            {state.phase === "running" ? (
              <>
                <span className="inline-flex h-7 items-center gap-2 rounded-full border border-primary/45 bg-primary/10 px-3 text-xs font-semibold text-accent-peach">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden />
                  {tJudge("statusRunning")}
                </span>
                <span className={cn(styles.mono, "hidden sm:inline")}>
                  {t("consoleTest", { n: Math.min(state.testsDone + 1, CONSOLE_TESTS), total: CONSOLE_TESTS })}
                </span>
              </>
            ) : null}
            {state.phase === "accepted" ? (
              <>
                <span
                  className={cn(
                    styles.pop,
                    "inline-flex h-7 items-center gap-1.5 rounded-full border border-emerald-400/35 bg-emerald-400/10 px-3 text-xs font-semibold text-emerald-300",
                  )}
                >
                  <Check className="h-3.5 w-3.5" aria-hidden />
                  {tJudge("verdict.accepted")}
                </span>
                <span className="hidden truncate sm:inline">Metro Card · {source.name}</span>
              </>
            ) : null}
          </div>
          <Button
            type="button"
            onClick={submit}
            disabled={busy}
            className={cn("h-9 shrink-0 px-4 text-[13px]", state.pressing && "scale-95 bg-primary/90")}
          >
            {t("consoleSubmit")}
          </Button>
        </div>

        <div className="mt-3 grid grid-cols-12 gap-1 sm:gap-1.5" aria-hidden>
          {Array.from({ length: CONSOLE_TESTS }, (_, index) => {
            const done = index < state.testsDone;
            const current = state.phase === "running" && index === state.testsDone;
            return (
              <span
                key={index}
                className={cn(
                  styles.mono,
                  "flex h-6 items-center justify-center rounded-md border text-[9.5px] font-semibold transition-colors duration-200 ease-out sm:h-7 sm:text-[10.5px]",
                  done
                    ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-300"
                    : current
                      ? "border-primary/75 bg-primary/10 text-accent-peach"
                      : "border-white/[0.06] bg-white/[0.04] text-neutral-600",
                )}
              >
                {index < CONSOLE_SAMPLES ? `S${index + 1}` : index - CONSOLE_SAMPLES + 1}
              </span>
            );
          })}
        </div>

        <div className={cn(styles.mono, "mt-3 flex flex-col justify-between gap-1 text-[11.5px] text-neutral-400 sm:flex-row sm:gap-3")}>
          <span>
            {state.phase === "accepted"
              ? t("consoleResult", { samples: CONSOLE_SAMPLES, hidden: CONSOLE_HIDDEN })
              : state.phase === "running"
                ? t("consoleProgress", {
                    samplesDone,
                    samples: CONSOLE_SAMPLES,
                    hiddenDone,
                    hidden: CONSOLE_HIDDEN,
                  })
                : t("consoleIdle", { samples: CONSOLE_SAMPLES, hidden: CONSOLE_HIDDEN })}
          </span>
          {state.phase === "accepted" ? <span>{source.perf}</span> : null}
        </div>
      </div>
    </section>
  );
}
